import {
  IFeotClockSnapshot,
  IFeotQuotaSnapshot,
} from '../qa-tools-service/feot-clock.interface';
import { IProjectionInputs, IQuotaInputs } from './feot-projection';

/**
 * Reads a dotted path out of a snapshot. An absent key reads as null on both
 * sides of the Before/After panel, so it never shows up as a change.
 */
export function readSnapshotPath(
  source: object | null,
  key: string
): string | number | boolean | null {
  if (!source) {
    return null;
  }
  const value = key
    .split('.')
    .reduce(
      (acc: Record<string, unknown> | undefined, part) =>
        acc?.[part] as Record<string, unknown> | undefined,
      source as unknown as Record<string, unknown>
    );
  return (value as string | number | boolean | null | undefined) ?? null;
}

/**
 * A layered quota carries an empty quota_title; its readable name is the
 * criteria, which is also what the marketplace quota grid shows.
 */
export function quotaLabel(quota: IFeotQuotaSnapshot): string {
  if (quota.quota_title) {
    return quota.quota_title;
  }
  const fromCriteria = (quota.criteria || [])
    .map((criterion) => {
      const conditions = (criterion.conditions || []).join(', ');
      return criterion.qualification_name
        ? `${criterion.qualification_name}: ${conditions}`
        : conditions;
    })
    .filter((label) => label.trim().length)
    .join(' · ');

  return fromCriteria || quota.quota_id;
}

export function buildQuotaInputs(
  snapshot: IFeotClockSnapshot | null
): IQuotaInputs[] {
  return (snapshot?.quotas || []).map((quota: IFeotQuotaSnapshot) => ({
    quotaId: quota.quota_id,
    quotaTitle: quotaLabel(quota),
    isActive: quota.isActive,
    percentage: quota.quantities.percentage,
    achieved: quota.quantities.achieved,
    maximum: quota.quantities.maximum,
    currentTarget: quota.quantities.current_target,
    currentlyOpen: quota.quantities.currently_open,
  }));
}

/**
 * Null whenever a required figure is missing, so the panel shows nothing rather
 * than a number built on zeros.
 */
export function buildProjectionInputs(
  snapshot: IFeotClockSnapshot | null
): IProjectionInputs | null {
  const goal = snapshot?.completes?.goal ?? null;
  const fielded = snapshot?.completes?.fielded ?? null;
  if (
    !snapshot ||
    goal === null ||
    fielded === null ||
    !snapshot.plannedFieldTimeInPacingUnits
  ) {
    return null;
  }
  return {
    goal,
    fielded,
    plannedFieldTime: snapshot.plannedFieldTimeInPacingUnits,
    elapsedFieldTime: snapshot.fulfilledFieldTimeInPacingUnits || 0,
    standardFulfilledFieldTime:
      snapshot.standardFulfilledFieldTimeInPacingUnits ?? 0,
    pacingInterval: snapshot.feot.pacingInterval || 1,
    flexibility: snapshot.feot.flexibility || 0,
  };
}
